import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HelpCircle, Play, Clock, Shuffle, Target, CheckCircle, XCircle, Lightbulb, MapPin, Move, AlignLeft, FileText, TrendingUp } from 'lucide-react';
import { exercisesApi } from '../services/api';

interface QuestionOption {
  id: string;
  text: string;
  order: number;
  isCorrect?: boolean;
  pairId?: string; // Para MATCHING
}

interface Question {
  id: string;
  question: string;
  type: 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'FILL_BLANK' | 'ESSAY' | 'ORDERING' | 'MULTIPLE_SELECT' | 'DRAG_DROP' | 'MATCHING' | 'HOTSPOT';
  points: number;
  order: number;
  explanation?: string;
  options: QuestionOption[];
  hotspotConfig?: {
    imageUrl: string;
    correctAreas: Array<{
      id: string;
      x: number;
      y: number;
      width: number;
      height: number;
      label: string;
    }>;
  };
  fillBlankConfig?: {
    questionText: string;
    blanks: Array<{
      position: number;
      correctAnswer: string;
    }>;
  };
  essayConfig?: {
    minWords: number;
    maxWords: number;
    evaluationCriteria: string[];
  };
}

interface QuizConfiguration {
  timeLimitMinutes?: number;
  allowRandomization?: boolean;
  passingScorePercentage?: number;
  showExplanations?: boolean;
  shuffleAnswers?: boolean;
}

interface LessonQuizData {
  hasQuestions: boolean;
  lessonTitle?: string;
  questions: Question[];
  configuration?: QuizConfiguration;
}

interface QuizResult {
  score: number;
  maxScore: number;
  percentage: number;
  isPassed: boolean;
  timeSpent: number;
  correctAnswers: number;
  totalQuestions: number;
  answerDetails: { [questionId: string]: {
    answer: string | string[];
    isCorrect: boolean;
    points: number;
    pointsEarned?: number;
    feedback?: string;
    confidence?: number;
    hotspotDistance?: number;
    wordCount?: number;
    structureAnalysis?: string;
    accuracy?: number;
  } };
}

interface LessonQuizProps {
  lessonId: string;
  onClose: () => void;
  isIndependentQuiz?: boolean;
}

const LessonQuiz: React.FC<LessonQuizProps> = ({ lessonId, onClose }) => {
  const [quizData, setQuizData] = useState<LessonQuizData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [questionId: string]: string | string[] }>({});
  const [showResults, setShowResults] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [quizStartTime, setQuizStartTime] = useState<Date | null>(null);
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shuffledQuestions, setShuffledQuestions] = useState<Question[]>([]);
  const [dropTargets, setDropTargets] = useState<{ [questionId: string]: { [targetId: string]: string } }>({});
  const [selectedHotspot, setSelectedHotspot] = useState<{ [questionId: string]: string | null }>({});
  const [matchingPairs, setMatchingPairs] = useState<{ [questionId: string]: { [leftId: string]: string } }>({});
  const imageRef = useRef<HTMLImageElement>(null);

  // Función para barajar preguntas
  const shuffleQuestions = useCallback((questions: Question[]): Question[] => {
    const shuffled = [...questions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // Función para barajar opciones
  const shuffleOptions = useCallback((options: QuestionOption[]): QuestionOption[] => {
    const shuffled = [...options];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  useEffect(() => {
    const fetchQuizData = async () => {
      try {
        setLoading(true);
        const response = await exercisesApi.getLessonQuestions(lessonId);
        const data = response.data;

        setQuizData(data);

        // Configurar aleatorización
        if (data.configuration?.allowRandomization) {
          setShuffledQuestions(shuffleQuestions(data.questions));
        } else {
          setShuffledQuestions(data.questions);
        }

        // Configurar temporizador
        if (data.configuration?.timeLimitMinutes && data.configuration.timeLimitMinutes > 0) {
          setTimeLeft(data.configuration.timeLimitMinutes * 60);
        }

        // Calcular puntuación máxima
        const maxScore = data.questions.reduce((sum: number, q: Question) => sum + q.points, 0);
        setQuizResult(prev => prev ? { ...prev, maxScore } : null);

        setQuizStartTime(new Date());
      } catch (error) {
        console.error('Error fetching quiz data:', error);
        setQuizData({ hasQuestions: false, questions: [] });
      } finally {
        setLoading(false);
      }
    };

    fetchQuizData();
  }, [lessonId, shuffleQuestions]);

  // Efecto para el temporizador
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || showResults) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev && prev <= 1) {
          clearInterval(timer);
          handleSubmitQuiz(); // Enviar automáticamente cuando se acaba el tiempo
          return 0;
        }
        return prev ? prev - 1 : null;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, showResults]);

  const handleAnswerChange = (questionId: string, answer: string | string[]) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  // Funciones para DRAG_DROP
  const handleDragStart = (e: React.DragEvent, questionId: string, itemId: string) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ questionId, itemId }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, questionId: string, targetId: string) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    if (data.questionId === questionId) {
      setDropTargets(prev => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          [targetId]: data.itemId
        }
      }));
    }
  };

  const handleRemoveDrop = (questionId: string, targetId: string) => {
    setDropTargets(prev => {
      const newTargets = { ...prev[questionId] };
      delete newTargets[targetId];
      return {
        ...prev,
        [questionId]: newTargets
      };
    });
  };

  // Funciones para MATCHING
  const handleMatchingSelect = (questionId: string, leftId: string, rightId: string) => {
    setMatchingPairs(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [leftId]: rightId
      }
    }));
  };

  // Funciones para HOTSPOT
  const handleHotspotClick = (e: React.MouseEvent, questionId: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setSelectedHotspot(prev => ({
      ...prev,
      [questionId]: `${Math.round(x)},${Math.round(y)}`
    }));
  };

  const isAnswerComplete = (questionId: string, questionType: string) => {
    switch (questionType) {
      case 'MULTIPLE_SELECT':
        return Array.isArray(answers[questionId]) && (answers[questionId] as string[]).length > 0;
      case 'DRAG_DROP':
        return dropTargets[questionId] && Object.keys(dropTargets[questionId]).length > 0;
      case 'MATCHING':
        return matchingPairs[questionId] && Object.keys(matchingPairs[questionId]).length > 0;
      case 'HOTSPOT':
        return selectedHotspot[questionId] !== null && selectedHotspot[questionId] !== undefined;
      case 'FILL_BLANK':
        const fillAnswer = answers[questionId];
        if (typeof fillAnswer === 'string') {
          return fillAnswer.trim() !== '';
        }
        if (Array.isArray(fillAnswer)) {
          return fillAnswer.every(answer => answer.trim() !== '');
        }
        return false;
      default:
        const answer = answers[questionId];
        return typeof answer === 'string' && answer.trim() !== '';
    }
  };

  const canProceedToNext = () => {
    const currentQuestion = shuffledQuestions[currentQuestionIndex];
    if (!currentQuestion) return false;
    return isAnswerComplete(currentQuestion.id, currentQuestion.type);
  };

  const calculateScore = useCallback(() => {
    if (!quizData) return {
      score: 0,
      maxScore: 0,
      percentage: 0,
      isPassed: false,
      correctAnswers: 0,
      totalQuestions: 0,
      answerDetails: {}
    };

    let score = 0;
    const maxScore = quizData.questions.reduce((sum: number, q: Question) => sum + q.points, 0);

    const answerDetails: { [questionId: string]: any } = {};

    let correctAnswers = 0;

    quizData.questions.forEach(question => {
      const userAnswer = answers[question.id];
      let isCorrect = false;
      let points = 0;
      let feedback = '';
      let confidence = 0;

      // Sistema inteligente de evaluación por tipo de pregunta
      switch (question.type) {
        case 'MULTIPLE_CHOICE':
          if (userAnswer) {
            // Buscar la opción correcta usando el campo isCorrect
            const correctOption = question.options.find(opt => opt.isCorrect);
            if (correctOption) {
              isCorrect = userAnswer === correctOption.id;
              points = isCorrect ? question.points : 0;
              confidence = isCorrect ? 1 : 0;
              feedback = isCorrect
                ? '¡Excelente! Respuesta correcta. 🎉'
                : `Respuesta incorrecta. La respuesta correcta es: ${correctOption.text}`;
            } else {
              // Fallback: si no hay isCorrect, asumimos la primera opción
              const correctOption = question.options[0];
              const correctAnswer = correctOption ? correctOption.id : userAnswer;
              isCorrect = userAnswer === correctAnswer;
              points = isCorrect ? question.points : 0;
              confidence = isCorrect ? 1 : 0;
              feedback = isCorrect
                ? '¡Excelente! Respuesta correcta. 🎉'
                : `Respuesta incorrecta. La respuesta correcta es: ${correctOption?.text || userAnswer}`;
            }
          }
          break;

        case 'TRUE_FALSE':
          if (userAnswer) {
            // Buscar la opción correcta usando el campo isCorrect
            const correctOption = question.options.find(opt => opt.isCorrect);
            if (correctOption) {
              isCorrect = userAnswer === correctOption.text;
              points = isCorrect ? question.points : 0;
              confidence = isCorrect ? 1 : 0;
              feedback = isCorrect
                ? '¡Excelente! Respuesta correcta. 🎉'
                : `Respuesta incorrecta. La respuesta correcta es: ${correctOption.text}. Sigue intentando 💪`;
            } else {
              // Fallback: si no hay isCorrect, asumimos la primera opción
              const correctAnswer = question.options.length > 0 ? question.options[0].text : 'Verdadero';
              isCorrect = userAnswer === correctAnswer;
              points = isCorrect ? question.points : 0;
              confidence = isCorrect ? 1 : 0;
              feedback = isCorrect
                ? '¡Excelente! Respuesta correcta. 🎉'
                : `Respuesta incorrecta. La respuesta correcta es: ${correctAnswer}. Sigue intentando 💪`;
            }
          }
          break;

        case 'MULTIPLE_SELECT':
          if (Array.isArray(userAnswer) && userAnswer.length > 0) {
            // Para multiple select, obtener los IDs de las opciones correctas
            const correctOptionIds = question.options.filter(opt => opt.isCorrect).map(opt => opt.id);
            const userAnswers = userAnswer as string[];

            // Verificar si todas las respuestas correctas fueron seleccionadas y no hay respuestas incorrectas
            const allCorrectSelected = correctOptionIds.every(opt => userAnswers.includes(opt));
            const noIncorrectSelected = userAnswers.every(ans => correctOptionIds.includes(ans));

            isCorrect = allCorrectSelected && noIncorrectSelected;
            points = isCorrect ? question.points : Math.round(question.points * 0.5); // Puntos parciales
            confidence = isCorrect ? 1 : 0.5;

            const correctOptionTexts = question.options.filter(opt => opt.isCorrect).map(opt => opt.text);

            if (isCorrect) {
              feedback = '¡Perfecto! Seleccionaste todas las opciones correctas. ⭐';
            } else if (allCorrectSelected && !noIncorrectSelected) {
              feedback = 'Buen intento, pero seleccionaste algunas opciones incorrectas. Las correctas son: ' + correctOptionTexts.join(', ');
            } else {
              feedback = 'Las respuestas correctas son: ' + correctOptionTexts.join(', ');
            }
          }
          break;

        case 'FILL_BLANK':
          if (question.fillBlankConfig && question.fillBlankConfig.blanks) {
            const userAnswers = Array.isArray(userAnswer) ? userAnswer as string[] : [userAnswer as string];
            const correctAnswers = question.fillBlankConfig.blanks.map(blank => blank.correctAnswer);

            let correctCount = 0;
            const evaluations: string[] = [];

            userAnswers.forEach((answer, index) => {
              if (index < correctAnswers.length) {
                const isBlankCorrect = answer.trim().toLowerCase() === correctAnswers[index].toLowerCase();
                if (isBlankCorrect) {
                  correctCount++;
                  evaluations.push(`Espacio ${index + 1}: ✅ Correcto`);
                } else {
                  evaluations.push(`Espacio ${index + 1}: ❌ Incorrecto (respuesta: "${correctAnswers[index]}")`);
                }
              }
            });

            const accuracy = correctCount / correctAnswers.length;
            isCorrect = accuracy >= 0.8; // 80% o más de respuestas correctas
            points = Math.round(question.points * accuracy);
            confidence = accuracy;

            feedback = evaluations.join(' | ') +
                       (isCorrect ? ' | ¡Buen trabajo!' : ' | Sigue practicando.');

            // Add accuracy for feedback
            (answerDetails as any)[question.id].accuracy = accuracy;
          } else {
            // Fallback: usar la lógica del backend (buscar opción correcta por isCorrect)
            const correctOption = question.options.find(opt => opt.isCorrect);
            if (correctOption && typeof userAnswer === 'string') {
              isCorrect = userAnswer.toLowerCase().trim() === correctOption.text.toLowerCase().trim();
              points = isCorrect ? question.points : 0;
              confidence = isCorrect ? 1 : 0;
              feedback = isCorrect
                ? '¡Excelente! Respuesta correcta. 🎉'
                : `Respuesta incorrecta. La respuesta correcta es: ${correctOption.text}`;
            } else if (typeof userAnswer === 'string' && userAnswer.trim()) {
              // Último fallback si no hay opciones con isCorrect
              isCorrect = true;
              points = question.points;
              confidence = 0.8;
              feedback = '¡Respuesta recibida! 📝';
            }
          }
          break;

        case 'ESSAY':
          if (typeof userAnswer === 'string' && userAnswer.trim()) {
            const wordCount = userAnswer.trim().split(/\s+/).length;
            const { minWords = 0, maxWords = 1000 } = question.essayConfig || {};

            // Evaluación básica de ensayo
            const hasGoodStructure = userAnswer.length > 100; // Mínimo de caracteres
            const hasProperLength = wordCount >= minWords && wordCount <= maxWords;

            isCorrect = hasGoodStructure && hasProperLength;
            points = hasGoodStructure ? Math.round(question.points * 0.7) : Math.round(question.points * 0.3);
            confidence = hasGoodStructure ? 0.7 : 0.3;

            feedback = hasGoodStructure
              ? `Buen ensayo (${wordCount} palabras). ${hasProperLength ? 'Dentro del límite requerido.' : `Intenta mantener entre ${minWords}-${maxWords} palabras.`}`
              : `Tu ensayo es muy corto. Intenta desarrollar más tus ideas (${wordCount}/${minWords} palabras).`;

            // Add word count and structure analysis for feedback
            (answerDetails as any)[question.id].wordCount = wordCount;
            (answerDetails as any)[question.id].structureAnalysis = hasGoodStructure ? 'Buena estructura' : 'Estructura básica';
          }
          break;

        case 'HOTSPOT':
          if (selectedHotspot[question.id]) {
            const [userX, userY] = selectedHotspot[question.id]!.split(',').map(Number);
            let bestMatch: any = null;
            let bestDistance = Infinity;

            // Encontrar el área correcta más cercana
            question.hotspotConfig?.correctAreas.forEach((area: any) => {
              const areaCenterX = area.x + (area.width / 2);
              const areaCenterY = area.y + (area.height / 2);
              const distance = Math.sqrt(Math.pow(userX - areaCenterX, 2) + Math.pow(userY - areaCenterY, 2));

              if (distance < bestDistance) {
                bestDistance = distance;
                bestMatch = area;
              }
            });

            // Considerar correcto si está dentro de un umbral de distancia
            const threshold = 15; // 15% de tolerancia
            isCorrect = bestDistance < threshold;
            points = isCorrect ? question.points : Math.round(question.points * 0.5);
            confidence = Math.max(0, 1 - (bestDistance / 50)); // Confianza basada en distancia

            if (bestMatch) {
              feedback = isCorrect
                ? `¡Excelente! Seleccionaste el área correcta: ${bestMatch.label || 'Área objetivo'}. 🎯`
                : `Cercano pero no exacto. El área correcta era: ${bestMatch.label || 'Área objetivo'}. La distancia fue de ${Math.round(bestDistance)}%.`;
            } else {
              feedback = 'No se encontró un área correcta cercana a tu selección.';
            }

            // Add hotspot distance for feedback
            if (bestDistance !== Infinity) {
              (answerDetails as any)[question.id].hotspotDistance = bestDistance;
            }
          }
          break;

        case 'DRAG_DROP':
          if (dropTargets[question.id]) {
            const userOrdering = Object.values(dropTargets[question.id]);
            const totalItems = userOrdering.length;
            let correctPositions = 0;

            // Verificar orden correcto (simplificado - asumir orden natural)
            userOrdering.forEach((itemId, index) => {
              if (question.options[index]?.id === itemId) {
                correctPositions++;
              }
            });

            const accuracy = correctPositions / totalItems;
            isCorrect = accuracy === 1;
            points = Math.round(question.points * accuracy);
            confidence = accuracy;

            feedback = accuracy === 1
              ? '¡Perfecto! Ordenaste todos los elementos correctamente. 🎉'
              : accuracy >= 0.7
              ? `Buen trabajo (${correctPositions}/${totalItems} correctos). Casi perfecto!`
              : `Sigue practicando (${correctPositions}/${totalItems} correctos).`;
          }
          break;

        case 'MATCHING':
          if (matchingPairs[question.id]) {
            const userMatches = matchingPairs[question.id];
            const totalPairs = Object.keys(userMatches).length;
            let correctPairs = 0;

            // Verificar cada par (simplificado)
            Object.entries(userMatches).forEach(() => {
              // Lógica de verificación - esto necesitaría la configuración correcta
              correctPairs++; // Temporal: asumir que algunos son correctos
            });

            const accuracy = correctPairs / totalPairs;
            isCorrect = accuracy >= 0.8;
            points = Math.round(question.points * accuracy);
            confidence = accuracy;

            feedback = accuracy === 1
              ? '¡Excelente! Relacionaste todas las parejas correctamente. ⭐'
              : `Buen intento (${correctPairs}/${totalPairs} parejas correctas).`;
          }
          break;

        default:
          // Lógica fallback para tipos no manejados
          if (userAnswer) {
            isCorrect = true;
            points = question.points;
            confidence = 0.5;
            feedback = 'Respuesta recibida.';
          }
      }

      if (isCorrect) {
        score += points;
        correctAnswers++;
      }

      answerDetails[question.id] = {
        answer: userAnswer || '',
        isCorrect,
        points,
        pointsEarned: isCorrect ? points : 0,
        feedback,
        confidence
      };
    });

    const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
    const passingScore = quizData.configuration?.passingScorePercentage || 70;
    const isPassed = percentage >= passingScore;

    return {
      score,
      maxScore,
      percentage,
      isPassed,
      correctAnswers,
      totalQuestions: quizData.questions.length,
      answerDetails
    };
  }, [quizData, answers, dropTargets, selectedHotspot, matchingPairs]);

  const handleSubmitQuiz = async () => {
    if (!quizData || !quizStartTime || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const timeSpent = Math.floor((new Date().getTime() - quizStartTime.getTime()) / 1000);

      // Enviar resultados al backend
      const simpleAnswers: { [questionId: string]: string } = {};
      Object.entries(answers).forEach(([questionId, answer]) => {
        simpleAnswers[questionId] = Array.isArray(answer) ? answer.join(',') : answer;
      });

      // Obtener resultados del backend (no calcular localmente)
      const backendResponse = await exercisesApi.submitQuiz(lessonId, simpleAnswers);

      // Usar los resultados del backend (el backend usa .data, .score, .totalPoints, etc.)
      const backendData = backendResponse.data.data || backendResponse.data;

      // Transformar el formato del backend al formato que espera el frontend
      const transformedResults: { [questionId: string]: any } = {};
      if (backendData.detailedResults) {
        backendData.detailedResults.forEach((detail: any) => {
          transformedResults[detail.questionId] = {
            answer: detail.userAnswer,
            isCorrect: detail.isCorrect,
            points: detail.maxPoints,
            pointsEarned: detail.points,
            feedback: detail.explanation
          };
        });
      }

      setQuizResult({
        score: backendData.score || backendData.totalPoints,
        maxScore: backendData.totalPoints,
        percentage: backendData.percentage,
        isPassed: backendData.passed,
        timeSpent,
        correctAnswers: backendData.correctAnswers,
        totalQuestions: backendData.totalQuestions,
        answerDetails: transformedResults
      });

      setShowResults(true);
    } catch (error) {
      console.error('Error submitting quiz:', error);
      // Fallback: usar cálculo local si el backend falla
      const result = calculateScore();
      const timeSpent = Math.floor((new Date().getTime() - quizStartTime.getTime()) / 1000);

      setQuizResult({
        ...result,
        timeSpent
      });
      setShowResults(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < shuffledQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      // Resetear estado al cambiar de pregunta
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      // Resetear estado al cambiar de pregunta
    }
  };

  const handleRetakeQuiz = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults(false);
    setQuizResult(null);
        setDropTargets({});
    setSelectedHotspot({});
    setMatchingPairs({});
    // Reset hints

    // Resetear temporizador
    if (quizData?.configuration?.timeLimitMinutes) {
      setTimeLeft(quizData.configuration.timeLimitMinutes * 60);
    }

    // Re-barajar preguntas si es necesario
    if (quizData?.configuration?.allowRandomization) {
      setShuffledQuestions(shuffleQuestions(quizData.questions));
    }

    setQuizStartTime(new Date());
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentQuestion = shuffledQuestions[currentQuestionIndex];
  const totalQuestions = shuffledQuestions.length;
  const progress = totalQuestions > 0 ? ((currentQuestionIndex + 1) / totalQuestions) * 100 : 0;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Cargando quiz...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!quizData?.hasQuestions || !currentQuestion) {
    return null;
  }

  if (showResults && quizResult) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            {/* Header con resultado */}
            <div className="text-center mb-6">
              <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 ${
                quizResult.percentage >= 90 ? 'bg-gradient-to-br from-yellow-100 to-yellow-200 animate-bounce' :
                quizResult.percentage >= 80 ? 'bg-gradient-to-br from-green-100 to-green-200 animate-pulse' :
                quizResult.isPassed ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {quizResult.percentage >= 90 ? (
                  <div className="text-4xl">🏆</div>
                ) : quizResult.percentage >= 80 ? (
                  <div className="text-4xl">⭐</div>
                ) : quizResult.isPassed ? (
                  <CheckCircle className="w-12 h-12 text-green-600" />
                ) : (
                  <XCircle className="w-12 h-12 text-red-600" />
                )}
              </div>
              <h2 className={`text-4xl font-bold mb-2 ${
                quizResult.percentage >= 90 ? 'text-yellow-600 animate-pulse' :
                quizResult.percentage >= 80 ? 'text-green-600' :
                quizResult.isPassed ? 'text-green-600' : 'text-red-600'
              }`}>
                {quizResult.percentage >= 90 ? '¡EXCELENTE! ¡Has sobresalido! 🎉' :
                 quizResult.percentage >= 80 ? '¡MUY BIEN! ¡Gran desempeño! 🌟' :
                 quizResult.isPassed ? '¡Quiz Aprobado! 🎯' : 'Sigue intentando 💪'}
              </h2>
              <p className={`text-lg mb-4 ${
                quizResult.percentage >= 90 ? 'text-yellow-700 font-medium' :
                quizResult.percentage >= 80 ? 'text-green-700 font-medium' :
                quizResult.isPassed ? 'text-green-600' : 'text-gray-600'
              }`}>
                {quizResult.percentage >= 90 ?
                  '¡Impresionante! Has demostrado un conocimiento excepcional. ¡Sigue así!' :
                 quizResult.percentage >= 80 ?
                  '¡Excelente trabajo! Tu dedicación ha dado grandes frutos.' :
                 quizResult.isPassed ?
                  '¡Felicidades! Has superado el desafío con éxito.' :
                  'No te preocupes, cada intento te acerca más al éxito. ¡Puedes hacerlo!'}
              </p>
              <p className="text-2xl font-bold mb-4 text-gray-800">
                {quizResult.score} de {quizResult.maxScore} puntos
                <span className={`ml-2 ${
                  quizResult.percentage >= 90 ? 'text-yellow-600' :
                  quizResult.percentage >= 80 ? 'text-green-600' :
                  quizResult.isPassed ? 'text-green-600' : 'text-red-600'
                }`}>
                  ({quizResult.percentage}%)
                </span>
              </p>
              <div className="flex items-center justify-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-full">
                  <Clock className="w-4 h-4" />
                  <span>Tiempo: {formatTime(quizResult.timeSpent)}</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-full">
                  <Target className="w-4 h-4" />
                  <span>Puntuación: {quizResult.score}/{quizResult.maxScore} pts</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-full">
                  <TrendingUp className="w-4 h-4" />
                  <span>Correctas: {quizResult.correctAnswers}/{quizResult.totalQuestions}</span>
                </div>
              </div>
            </div>

            {/* Resultados detallados por pregunta */}
            <div className="space-y-4 mb-6">
              {shuffledQuestions.map((question, index) => {
                const result = quizResult.answerDetails[question.id];
                const isAnswered = result && result.answer && (result.answer.toString().trim() !== '' ||
                                            (Array.isArray(result.answer) && result.answer.length > 0));

                return (
                  <div key={question.id} className={`border rounded-lg p-4 ${
                    result?.isCorrect ? 'border-green-200 bg-green-50' :
                    isAnswered ? 'border-red-200 bg-red-50' : 'border-orange-200 bg-orange-50'
                  }`}>
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-medium text-gray-800">
                        {index + 1}. {question.question}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          question.type === 'MULTIPLE_CHOICE' ? 'bg-blue-100 text-blue-800' :
                          question.type === 'TRUE_FALSE' ? 'bg-purple-100 text-purple-800' :
                          question.type === 'FILL_BLANK' ? 'bg-yellow-100 text-yellow-800' :
                          question.type === 'ESSAY' ? 'bg-indigo-100 text-indigo-800' :
                          question.type === 'MULTIPLE_SELECT' ? 'bg-pink-100 text-pink-800' :
                          question.type === 'DRAG_DROP' ? 'bg-green-100 text-green-800' :
                          question.type === 'MATCHING' ? 'bg-orange-100 text-orange-800' :
                          question.type === 'HOTSPOT' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {question.type === 'MULTIPLE_CHOICE' ? 'Opción Múltiple' :
                           question.type === 'TRUE_FALSE' ? 'Verdadero/Falso' :
                           question.type === 'FILL_BLANK' ? 'Completar' :
                           question.type === 'ESSAY' ? 'Ensayo' :
                           question.type === 'MULTIPLE_SELECT' ? 'Múltiples Selecciones' :
                           question.type === 'DRAG_DROP' ? 'Arrastrar y Soltar' :
                           question.type === 'MATCHING' ? 'Relacionar' :
                           question.type === 'HOTSPOT' ? 'Punto Caliente' :
                           question.type}
                        </span>
                        <span className="text-sm font-medium">
                          {result?.pointsEarned || 0}/{question.points} pt{question.points !== 1 ? 's' : ''}
                        </span>
                        {result?.isCorrect ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : isAnswered ? (
                          <XCircle className="w-5 h-5 text-red-600" />
                        ) : (
                          <span className="text-orange-600">⚠</span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* Tu respuesta */}
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Tu respuesta:</span>
                        <span className={`ml-2 ${
                          result?.isCorrect ? 'text-green-800 font-medium' :
                          isAnswered ? 'text-red-800' : 'text-orange-600 italic'
                        }`}>
                          {(() => {
                            // Para MULTIPLE_CHOICE y MULTIPLE_SELECT, necesitamos mostrar el texto de la opción, no el ID
                            if (question.type === 'MULTIPLE_CHOICE' && result?.answer) {
                              const selectedOption = question.options.find(opt => opt.id === result.answer);
                              return selectedOption ? selectedOption.text : result.answer;
                            }
                            if (question.type === 'MULTIPLE_SELECT' && result?.answer) {
                              // Handle both array and string formats
                              let answerIds: string[];
                              if (Array.isArray(result.answer)) {
                                answerIds = result.answer;
                              } else if (typeof result.answer === 'string') {
                                answerIds = result.answer.split(',').map(id => id.trim());
                              } else {
                                answerIds = [];
                              }

                              const selectedTexts = answerIds.map(answerId => {
                                const option = question.options.find(opt => opt.id === answerId);
                                return option ? option.text : answerId;
                              });
                              return selectedTexts.join(', ');
                            }
                            return Array.isArray(result?.answer) ? result.answer.join(', ') : result?.answer || 'No respondida';
                          })()}
                        </span>
                      </div>

                      {/* Retroalimentación inteligente */}
                      {result?.feedback && (
                        <div className={`border-l-4 p-3 text-sm ${
                          result?.isCorrect ? 'border-green-400 bg-green-50' :
                          isAnswered ? 'border-yellow-400 bg-yellow-50' : 'border-blue-400 bg-blue-50'
                        }`}>
                          <div className="flex items-start gap-2">
                            {result?.isCorrect ? (
                              <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            ) : (
                              <Lightbulb className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                            )}
                            <div>
                              <span className={`font-medium ${
                                result?.isCorrect ? 'text-green-800' : 'text-yellow-800'
                              }`}>
                                {result?.isCorrect ? '¡Excelente!' : 'Retroalimentación:'}
                              </span>
                              <span className={`ml-2 ${
                                result?.isCorrect ? 'text-green-700' : 'text-yellow-700'
                              }`}>
                                {result.feedback}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Nivel de confianza */}
                      {result?.confidence !== undefined && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium text-gray-700">Confianza:</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                            <div
                              className={`h-2 rounded-full transition-all duration-500 ${
                                result.confidence > 0.7 ? 'bg-green-500' :
                                result.confidence > 0.4 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.round(result.confidence * 100)}%` }}
                            ></div>
                          </div>
                          <span className={`text-xs font-medium ${
                            result.confidence > 0.7 ? 'text-green-600' :
                            result.confidence > 0.4 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {Math.round(result.confidence * 100)}%
                          </span>
                        </div>
                      )}

                      {/* Explicación adicional */}
                      {quizData.configuration?.showExplanations && question.explanation && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                          <div className="flex items-start gap-2">
                            <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium text-blue-800">Explicación:</span>
                              <span className="text-blue-700 ml-2">{question.explanation}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Información específica por tipo de pregunta */}
                      {question.type === 'HOTSPOT' && result?.hotspotDistance !== undefined && (
                        <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm">
                          <div className="flex items-start gap-2">
                            <Target className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium text-purple-800">Precisión:</span>
                              <span className="text-purple-700 ml-2">
                                Distancia del objetivo: {Math.round(result.hotspotDistance)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {question.type === 'ESSAY' && result?.wordCount !== undefined && (
                        <div className="bg-indigo-50 border border-indigo-200 rounded p-3 text-sm">
                          <div className="flex items-start gap-2">
                            <FileText className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium text-indigo-800">Análisis del ensayo:</span>
                              <span className="text-indigo-700 ml-2">
                                Palabras: {result.wordCount} | Estructura: {result.structureAnalysis || 'Analizada'}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {question.type === 'FILL_BLANK' && result?.accuracy !== undefined && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium text-yellow-800">Precisión:</span>
                              <span className="text-yellow-700 ml-2">
                                {Math.round(result.accuracy * 100)}% de respuestas correctas
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Botones de acción */}
            <div className="flex gap-3">
              <button
                onClick={handleRetakeQuiz}
                className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <Play className="w-4 h-4 mr-2" />
                Repetir Quiz
              </button>
              <button
                onClick={onClose}
                className="flex-1 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header con temporizador y progreso */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-blue-500" />
                Quiz: {quizData.lessonTitle}
              </h2>
              <div className="flex items-center gap-4">
                {timeLeft !== null && timeLeft > 0 && (
                  <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                    timeLeft < 60 ? 'bg-red-100 text-red-800 animate-pulse' : 'bg-blue-100 text-blue-800'
                  }`}>
                    <Clock className="w-4 h-4" />
                    <span>{formatTime(timeLeft)}</span>
                  </div>
                )}
                {quizData.configuration?.allowRandomization && (
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                    <Shuffle className="w-4 h-4" />
                    <span>Aleatorio</span>
                  </div>
                )}
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Barra de progreso */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Pregunta {currentQuestionIndex + 1} de {totalQuestions}</span>
              <span className="font-medium">{currentQuestion.points} pt{currentQuestion.points !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {/* Pregunta actual */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-start">
              <HelpCircle className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
              {currentQuestion.question}
            </h3>

            {/* Opciones según el tipo de pregunta */}
            <div className="space-y-3">
              {currentQuestion.type === 'MULTIPLE_CHOICE' && (
                <>
                  {(quizData.configuration?.shuffleAnswers ? shuffleOptions(currentQuestion.options) : currentQuestion.options).map((option) => (
                    <label
                      key={option.id}
                      className="flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                    >
                      <input
                        type="radio"
                        name={`question-${currentQuestion.id}`}
                        value={option.id}
                        checked={answers[currentQuestion.id] === option.id}
                        onChange={() => handleAnswerChange(currentQuestion.id, option.id)}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="ml-3 text-gray-700 font-medium">{option.text}</span>
                    </label>
                  ))}
                </>
              )}

              {currentQuestion.type === 'MULTIPLE_SELECT' && (
                <>
                  {(quizData.configuration?.shuffleAnswers ? shuffleOptions(currentQuestion.options) : currentQuestion.options).map((option) => (
                    <label
                      key={option.id}
                      className="flex items-center p-4 border-2 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-all"
                    >
                      <input
                        type="checkbox"
                        value={option.id}
                        checked={Array.isArray(answers[currentQuestion.id]) ?
                                (answers[currentQuestion.id] as string[]).includes(option.id) : false}
                        onChange={(e) => {
                          const currentAnswers = Array.isArray(answers[currentQuestion.id]) ?
                            (answers[currentQuestion.id] as string[]) : [];
                          if (e.target.checked) {
                            handleAnswerChange(currentQuestion.id, [...currentAnswers, option.id]);
                          } else {
                            handleAnswerChange(currentQuestion.id, currentAnswers.filter(a => a !== option.id));
                          }
                        }}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-3 text-gray-700 font-medium">{option.text}</span>
                    </label>
                  ))}
                </>
              )}

              {currentQuestion.type === 'TRUE_FALSE' && (
                <div className="grid grid-cols-2 gap-4">
                  <label
                    className={`flex flex-col items-center p-6 border-2 rounded-lg cursor-pointer transition-all ${
                      answers[currentQuestion.id] === 'Verdadero'
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
                      <span className="text-green-600 font-bold text-xl">✓</span>
                    </div>
                    <input
                      type="radio"
                      name={`question-${currentQuestion.id}`}
                      value="Verdadero"
                      checked={answers[currentQuestion.id] === 'Verdadero'}
                      onChange={() => handleAnswerChange(currentQuestion.id, 'Verdadero')}
                      className="sr-only"
                    />
                    <span className="text-gray-700 font-semibold">Verdadero</span>
                  </label>

                  <label
                    className={`flex flex-col items-center p-6 border-2 rounded-lg cursor-pointer transition-all ${
                      answers[currentQuestion.id] === 'Falso'
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-3">
                      <span className="text-red-600 font-bold text-xl">✗</span>
                    </div>
                    <input
                      type="radio"
                      name={`question-${currentQuestion.id}`}
                      value="Falso"
                      checked={answers[currentQuestion.id] === 'Falso'}
                      onChange={() => handleAnswerChange(currentQuestion.id, 'Falso')}
                      className="sr-only"
                    />
                    <span className="text-gray-700 font-semibold">Falso</span>
                  </label>
                </div>
              )}

              {currentQuestion.type === 'FILL_BLANK' && (
                <div className="space-y-4 border-2 border-dashed border-yellow-300 rounded-xl p-6 bg-gradient-to-br from-yellow-50 to-amber-50">
                  <div className="flex items-center gap-2 mb-4">
                    <AlignLeft className="w-6 h-6 text-yellow-600" />
                    <h4 className="text-lg font-semibold text-gray-800">Completar los Espacios</h4>
                  </div>

                  {/* Mostrar el texto con espacios detectados */}
                  {currentQuestion.fillBlankConfig && currentQuestion.fillBlankConfig.blanks.length > 0 ? (
                    <div className="space-y-4">
                      <p className="text-gray-700 mb-4">
                        {currentQuestion.question.split(/_{3,}/).map((part, index, array) => (
                          <span key={index}>
                            {part}
                            {index < array.length - 1 && (
                              <input
                                type="text"
                                placeholder={`Respuesta ${index + 1}`}
                                value={(answers[currentQuestion.id] as string[] || [])[index] || ''}
                                onChange={(e) => {
                                  const currentAnswers = answers[currentQuestion.id] as string[] || [];
                                  const newAnswers = [...currentAnswers];
                                  newAnswers[index] = e.target.value;
                                  handleAnswerChange(currentQuestion.id, newAnswers);
                                }}
                                className="mx-2 px-3 py-1 border-2 border-yellow-300 rounded-md focus:border-yellow-500 focus:bg-yellow-100 transition-colors text-center min-w-[120px]"
                              />
                            )}
                          </span>
                        ))}
                      </p>
                      <p className="text-sm text-yellow-600">
                        {currentQuestion.fillBlankConfig.blanks.length} espacio(s) para completar
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Escribe tu respuesta aquí..."
                        value={answers[currentQuestion.id] as string || ''}
                        onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                        className="w-full p-4 border-2 border-yellow-300 rounded-lg focus:border-yellow-500 focus:bg-yellow-100 transition-colors text-gray-700 placeholder-gray-400"
                      />
                      <p className="text-sm text-yellow-600">
                        Escribe la respuesta completa en el espacio proporcionado
                      </p>
                    </div>
                  )}
                </div>
              )}

              {currentQuestion.type === 'ESSAY' && (
                <div className="space-y-4 border-2 border-dashed border-indigo-300 rounded-xl p-6 bg-gradient-to-br from-indigo-50 to-purple-50">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="w-6 h-6 text-indigo-600" />
                    <h4 className="text-lg font-semibold text-gray-800">Respuesta de Ensayo</h4>
                  </div>

                  <textarea
                    placeholder="Escribe tu respuesta detallada aquí..."
                    value={answers[currentQuestion.id] as string || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    rows={8}
                    className="w-full p-4 border-2 border-indigo-300 rounded-lg focus:border-indigo-500 focus:bg-indigo-50 transition-colors text-gray-700 placeholder-gray-400 resize-none"
                  />

                  {currentQuestion.essayConfig && (
                    <div className="space-y-2 text-sm text-indigo-600">
                      {currentQuestion.essayConfig.minWords > 0 && (
                        <p>• Mínimo: {currentQuestion.essayConfig.minWords} palabras</p>
                      )}
                      {currentQuestion.essayConfig.maxWords > 0 && (
                        <p>• Máximo: {currentQuestion.essayConfig.maxWords} palabras</p>
                      )}
                      {currentQuestion.essayConfig.evaluationCriteria.length > 0 && (
                        <div>
                          <p className="font-medium mb-1">Criterios de evaluación:</p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            {currentQuestion.essayConfig.evaluationCriteria.map((criterion, index) => (
                              <li key={index}>{criterion}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-sm text-indigo-500">
                    Proporciona una respuesta detallada y bien estructurada
                  </p>
                </div>
              )}

              {currentQuestion.type === 'ORDERING' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-3">Arrastra y ordena los elementos:</p>
                  {(quizData.configuration?.shuffleAnswers ? shuffleOptions(currentQuestion.options) : currentQuestion.options).map((option, index) => (
                    <div
                      key={option.id}
                      className="flex items-center p-4 border-2 border-gray-200 rounded-lg bg-gray-50"
                    >
                      <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold mr-3">
                        {index + 1}
                      </span>
                      <span className="text-gray-700 font-medium">{option.text}</span>
                    </div>
                  ))}
                </div>
              )}

              {currentQuestion.type === 'DRAG_DROP' && (
                <div className="space-y-4 border-2 border-dashed border-green-300 rounded-xl p-6 bg-gradient-to-br from-green-50 to-emerald-50">
                  <div className="flex items-center gap-2 mb-4">
                    <Move className="w-6 h-6 text-green-600" />
                    <h4 className="text-lg font-semibold text-gray-800">Arrastrar y Soltar</h4>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">Arrastra los elementos a sus posiciones correctas:</p>

                  {/* Elementos para arrastrar */}
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Elementos:</h5>
                    <div className="flex flex-wrap gap-2">
                      {currentQuestion.options
                        .filter(option => !Object.values(dropTargets[currentQuestion.id] || {}).includes(option.id))
                        .map((option) => (
                          <div
                            key={option.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, currentQuestion.id, option.id)}
                            className="px-4 py-2 bg-green-100 border-2 border-green-300 rounded-lg cursor-move hover:bg-green-200 transition-colors text-green-800 font-medium"
                          >
                            {option.text}
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Áreas de destino */}
                  <div className="space-y-3">
                    <h5 className="text-sm font-medium text-gray-700 mb-3">Destinos:</h5>
                    {Array.from({ length: currentQuestion.options.length }, (_, i) => {
                      const targetId = `target-${i}`;
                      const droppedItem = dropTargets[currentQuestion.id]?.[targetId];
                      const option = currentQuestion.options.find(opt => opt.id === droppedItem);

                      return (
                        <div
                          key={targetId}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, currentQuestion.id, targetId)}
                          className={`min-h-[60px] p-4 border-2 rounded-lg transition-colors ${
                            droppedItem
                              ? 'border-green-500 bg-green-50'
                              : 'border-dashed border-gray-300 hover:border-green-400 hover:bg-green-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">Posición {i + 1}</span>
                            {option ? (
                              <div className="flex items-center gap-2">
                                <span className="text-green-700 font-medium">{option.text}</span>
                                <button
                                  onClick={() => handleRemoveDrop(currentQuestion.id, targetId)}
                                  className="text-red-500 hover:text-red-700 text-sm"
                                >
                                  ×
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">Arrastra un elemento aquí</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {currentQuestion.type === 'MATCHING' && (
                <div className="space-y-4 border-2 border-dashed border-orange-300 rounded-xl p-6 bg-gradient-to-br from-orange-50 to-yellow-50">
                  <div className="flex items-center gap-2 mb-4">
                    <Target className="w-6 h-6 text-orange-600" />
                    <h4 className="text-lg font-semibold text-gray-800">Relacionar Columnas</h4>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">Relaciona los elementos de la izquierda con los de la derecha:</p>

                  <div className="grid grid-cols-2 gap-6">
                    {/* Columna izquierda */}
                    <div className="space-y-3">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Columna A</h5>
                      {currentQuestion.options
                        .filter(option => option.pairId)
                        .map((leftOption) => {
                          const matchedRightId = matchingPairs[currentQuestion.id]?.[leftOption.id];
                          const rightOption = currentQuestion.options.find(opt => opt.id === matchedRightId);

                          return (
                            <div
                              key={leftOption.id}
                              className={`p-4 border-2 rounded-lg transition-colors ${
                                rightOption
                                  ? 'border-orange-500 bg-orange-50'
                                  : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50 cursor-pointer'
                              }`}
                              onClick={() => {
                                // En una implementación real, aquí se mostraría un modal o selector
                                const rightOptions = currentQuestion.options
                                  .filter(opt => opt.pairId && opt.pairId !== leftOption.pairId)
                                  .filter(opt => !Object.values(matchingPairs[currentQuestion.id] || {}).includes(opt.id));

                                if (rightOptions.length > 0) {
                                  const selectedRight = rightOptions[0]; // Simplificado - en real sería un selector
                                  handleMatchingSelect(currentQuestion.id, leftOption.id, selectedRight.id);
                                }
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-gray-700 font-medium">{leftOption.text}</span>
                                {rightOption && (
                                  <span className="text-orange-600 text-sm">✓</span>
                                )}
                              </div>
                              {rightOption && (
                                <div className="mt-2 text-sm text-orange-600">
                                  Relacionado con: {rightOption.text}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>

                    {/* Columna derecha */}
                    <div className="space-y-3">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Columna B</h5>
                      {currentQuestion.options
                        .filter(option => option.pairId)
                        .filter(option => !Object.values(matchingPairs[currentQuestion.id] || {}).includes(option.id))
                        .map((rightOption) => (
                          <div
                            key={rightOption.id}
                            className="p-4 border-2 border-gray-200 rounded-lg bg-gray-50"
                          >
                            <span className="text-gray-700">{rightOption.text}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              )}

              {currentQuestion.type === 'HOTSPOT' && (
                <div className="space-y-4 border-2 border-dashed border-red-300 rounded-xl p-6 bg-gradient-to-br from-red-50 to-pink-50">
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin className="w-6 h-6 text-red-600" />
                    <h4 className="text-lg font-semibold text-gray-800">Punto Caliente Interactivo</h4>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">Haz clic en el área correcta de la imagen:</p>

                  {currentQuestion.hotspotConfig?.imageUrl ? (
                    <div className="space-y-4">
                      <div className="relative">
                        <img
                          ref={imageRef}
                          src={currentQuestion.hotspotConfig.imageUrl}
                          alt="Imagen para punto caliente"
                          className="w-full max-w-2xl mx-auto rounded-lg border-2 border-gray-300 cursor-crosshair"
                          onClick={(e) => handleHotspotClick(e, currentQuestion.id)}
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                        />

                        {/* Mostrar selección del usuario */}
                        {selectedHotspot[currentQuestion.id] && (
                          <div
                            className="absolute w-6 h-6 bg-red-500 rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2"
                            style={{
                              left: selectedHotspot[currentQuestion.id]?.split(',')[0] + '%',
                              top: selectedHotspot[currentQuestion.id]?.split(',')[1] + '%'
                            }}
                          >
                            <div className="w-full h-full bg-red-500 rounded-full animate-ping opacity-75"></div>
                          </div>
                        )}

                        {/* Mostrar áreas correctas (solo para demostración) */}
                        {currentQuestion.hotspotConfig.correctAreas.map((area) => (
                          <div
                            key={area.id}
                            className="absolute border-2 border-green-500 border-dashed rounded"
                            style={{
                              left: area.x + '%',
                              top: area.y + '%',
                              width: area.width + '%',
                              height: area.height + '%'
                            }}
                          >
                            <span className="absolute -top-6 left-0 text-xs bg-green-500 text-white px-1 rounded">
                              {area.label}
                            </span>
                          </div>
                        ))}
                      </div>

                      {selectedHotspot[currentQuestion.id] && (
                        <div className="text-center">
                          <p className="text-sm text-red-600">
                            Seleccionaste: {selectedHotspot[currentQuestion.id]}
                          </p>
                          <button
                            onClick={() => setSelectedHotspot(prev => ({ ...prev, [currentQuestion.id]: null }))}
                            className="mt-2 text-sm text-gray-500 hover:text-gray-700"
                          >
                            Cambiar selección
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500">No hay imagen disponible para esta pregunta</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Navegación */}
          <div className="flex justify-between items-center">
            <button
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Anterior
            </button>

            <div className="flex gap-2">
              {currentQuestionIndex === totalQuestions - 1 ? (
                <button
                  onClick={handleSubmitQuiz}
                  disabled={!canProceedToNext() || isSubmitting}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Target className="w-4 h-4 mr-2" />
                      Enviar Quiz
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleNextQuestion}
                  disabled={!canProceedToNext()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Siguiente
                </button>
              )}
            </div>
          </div>

          {/* Indicador de progreso */}
          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              {canProceedToNext() ? (
                <span className="text-green-600">✓ Pregunta respondida</span>
              ) : (
                <span className="text-orange-600">⚠ Responde la pregunta para continuar</span>
              )}
            </p>
            <div className="mt-2 text-xs text-gray-400">
              Puntuación actual: {Object.keys(answers).length}/{totalQuestions} preguntas respondidas
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonQuiz;